import { useEffect } from 'react';
import { useCan } from '@/lib/can';
import { useHotkey } from '@/lib/hooks';
import { t } from '@/lib/i18n';
import { setAskHandler, Sheet, useShellLayout } from '@/ui';
import s from './Assistant.module.css';
import { AssistantPanel } from './AssistantPanel';
import { closeAssistant, openAssistant, toggleAssistant, useAssistantState } from './store';

/** Is the assistant showing in the current layout? */
export function useAssistantVisible(): boolean {
  const layout = useShellLayout();
  const { open, docked } = useAssistantState();
  return layout === 'ultrawide' ? docked : open;
}

/**
 * Places the one assistant panel for the layout (IA §4): docked column on ultrawide (rendered
 * by the shell grid via `DockedAssistant`), slide-over on laptop, bottom sheet on phone.
 * Ctrl/Cmd+J toggles it everywhere.
 */
export function AssistantHost() {
  const allowed = useCan('core.ai.use');
  const layout = useShellLayout();
  const ultrawide = layout === 'ultrawide';
  const { open } = useAssistantState();

  useHotkey('mod+j', () => toggleAssistant(ultrawide), allowed);

  useEffect(() => {
    if (!allowed) return;
    setAskHandler((prompt) => openAssistant({ prompt, ultrawide }));
    return () => setAskHandler(null);
  }, [allowed, ultrawide]);

  useEffect(() => {
    if (layout !== 'laptop' || !open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && closeAssistant(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [layout, open]);

  if (!allowed || ultrawide) return null;
  if (layout === 'phone') {
    return (
      <Sheet
        open={open}
        onOpenChange={(o) => (o ? openAssistant() : closeAssistant(false))}
        title={t('Assistant')}
        side="bottom"
        size="lg"
        showTitle={false}
        closeButton={false}
        flush
        className={s.sheet}
      >
        <AssistantPanel onClose={() => closeAssistant(false)} />
      </Sheet>
    );
  }
  return open ? (
    <aside className={s.slideOver}>
      <AssistantPanel onClose={() => closeAssistant(false)} />
    </aside>
  ) : null;
}

/** The ultrawide docked column (the shell puts it in its own grid column). */
export function DockedAssistant() {
  const allowed = useCan('core.ai.use');
  const visible = useAssistantVisible();
  if (!allowed || !visible) return null;
  return (
    <aside className={s.docked}>
      <AssistantPanel onClose={() => closeAssistant(true)} />
    </aside>
  );
}
